import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { scrollToBooking } from "../utils/scrollToSection";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  LocationOn as LocationIcon,
  CheckCircle as CheckIcon,
  Event as EventIcon,
  MusicNote as MusicIcon,
  Star as StarIcon,
  ArrowForward as ArrowIcon,
  NavigateNext as NavNextIcon
} from "@mui/icons-material";
import { getInternationalCity } from "../data/locationData";
import Navbar from "../components/navbar.jsx";
import Footer from "../components/footer";
import BookingForm from "../components/bookingForm";

const InternationalCityPage = () => {
  const { continentSlug, countrySlug, citySlug } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const result = getInternationalCity(continentSlug, countrySlug, citySlug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [continentSlug, countrySlug, citySlug]);

  if (!result) {
    return (
      <>
        <Navbar />
        <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
          <Typography variant="h4">Location not found</Typography>
          <Button component={Link} to="/" sx={{ mt: 2 }}>Return Home</Button>
        </Container>
        <Footer />
      </>
    );
  }

  const { city, country, continent } = result;

  // Other cities in same country for cross-linking
  const siblingCities = country.cities.filter(c => c.slug !== city.slug);


  return (
    <>
      <Helmet>
        <title>{city.metaTitle}</title>
        <meta name="description" content={city.metaDescription} />
        <meta name="keywords" content={`dhol players ${city.name}, dhol drummers ${city.name}, ${city.name} wedding dhol, Asian wedding entertainment ${city.name}, Indian wedding drummers ${city.name}, Punjabi dhol ${city.name}, dhol hire ${city.name}`} />
        <link rel="canonical" href={`https://imperialdrummers.com/dhol-players/${continent.slug}/${country.slug}/${city.slug}`} />

        <meta property="og:title" content={city.metaTitle} />
        <meta property="og:description" content={city.metaDescription} />
        <meta property="og:url" content={`https://imperialdrummers.com/dhol-players/${continent.slug}/${country.slug}/${city.slug}`} />
        <meta property="og:type" content="website" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": `Imperial Drummers - ${city.name}`,
            "description": city.metaDescription,
            "url": `https://imperialdrummers.com/dhol-players/${continent.slug}/${country.slug}/${city.slug}`,
            "provider": {
              "@type": "Organization",
              "name": "Imperial Drummers",
              "url": "https://imperialdrummers.com"
            },
            "areaServed": {
              "@type": "City",
              "name": city.name,
              "containedInPlace": {
                "@type": "Country",
                "name": country.name
              }
            },
            "serviceType": "Dhol Entertainment"
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://imperialdrummers.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": continent.name,
                "item": `https://imperialdrummers.com/dhol-players/${continent.slug}`
              },
              {
                "@type": "ListItem",
                "position": 3,
                "name": country.name,
                "item": `https://imperialdrummers.com/dhol-players/${continent.slug}/${country.slug}`
              },
              {
                "@type": "ListItem",
                "position": 4,
                "name": city.name,
                "item": `https://imperialdrummers.com/dhol-players/${continent.slug}/${country.slug}/${city.slug}`
              }
            ]
          })}
        </script>
      </Helmet>

      <Navbar />

      <Box component="main">
        {/* Hero Section */}
        <Box sx={{
          background: 'linear-gradient(135deg, #5C005D 0%, #1a0a2e 100%)',
          color: 'white',
          pt: { xs: 14, md: 18 },
          pb: { xs: 8, md: 12 },
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Container maxWidth="lg">
            {/* Breadcrumb */}
            <Breadcrumbs
              separator={<NavNextIcon sx={{ color: 'rgba(255,255,255,0.5)' }} />}
              sx={{ mb: 3 }}
            >
              <Link to="/" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>
                Home
              </Link>
              <Link
                to={`/dhol-players/${continent.slug}`}
                style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}
              >
                {continent.name}
              </Link>
              <Link
                to={`/dhol-players/${continent.slug}/${country.slug}`}
                style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}
              >
                {country.name}
              </Link>
              <Typography sx={{ color: '#ffd700' }}>
                {city.name}
              </Typography>
            </Breadcrumbs>

            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={7}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <LocationIcon sx={{ mr: 1, color: '#ffd700' }} />
                  <Typography variant="subtitle1" sx={{ color: '#ffd700' }}>
                    {city.name}, {country.name}
                  </Typography>
                </Box>

                <Typography
                  variant={isMobile ? "h3" : "h1"}
                  component="h1"
                  sx={{ fontWeight: 'bold', mb: 3 }}
                >
                  Professional Dhol Players in {city.name}
                </Typography>

                <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, lineHeight: 1.7 }}>
                  {city.description}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={scrollToBooking}
                    startIcon={<EventIcon />}
                    color="gold"
                    sx={{
                      px: 4,
                      py: 1.5,
                      boxShadow: '0 8px 20px rgba(255, 215, 0, 0.3)',
                    }}
                  >
                    Get Free Quote
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    component={Link}
                    to="/"
                    sx={{
                      px: 4,
                      py: 1.5,
                      borderColor: 'white',
                      color: 'white',
                      '&:hover': { borderColor: '#ffd700', color: '#ffd700' }
                    }}
                  >
                    View Our Work
                  </Button>
                </Box>
              </Grid>

              <Grid item xs={12} md={5}>
                <Paper sx={{
                  p: 3,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  backdropFilter: 'blur(10px)',
                  borderRadius: 2,
                  border: '1px solid rgba(255,255,255,0.2)'
                }}>
                  <Typography variant="h6" sx={{ color: '#ffd700', mb: 2, fontWeight: 'bold' }}>
                    Why Choose Us in {city.name}?
                  </Typography>
                  <List dense>
                    {[
                      "Professional UK-trained dhol players",
                      "Authentic Punjabi & Bollywood performances",
                      "International travel experience",
                      "Luxury outfit options available",
                      "Flexible packages for all budgets"
                    ].map((item, i) => (
                      <ListItem key={i} sx={{ px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 36 }}>
                          <CheckIcon sx={{ color: '#ffd700' }} />
                        </ListItemIcon>
                        <ListItemText primary={item} sx={{ color: 'white' }} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Services Section */}
        <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: '#f8f5fb' }}>
          <Container maxWidth="lg">
            <Typography variant="h2" align="center" sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '1.75rem', md: '3rem' } }}>
              Dhol Entertainment Services in {city.name}
            </Typography>
            <Typography variant="h6" align="center" sx={{ color: '#666', mb: 6, maxWidth: 700, mx: 'auto' }}>
              Professional dhol players available for all types of celebrations in {city.name}, {country.name}
            </Typography>

            <Grid container spacing={4}>
              {[
                {
                  title: "Destination Wedding Dhol",
                  description: `Professional dhol drummers for baraat entrance, milni ceremony, and wedding celebrations in ${city.name}. Perfect for South Asian destination weddings in ${country.name}.`,
                  icon: <MusicIcon sx={{ fontSize: 40 }} />
                },
                {
                  title: "Corporate Events",
                  description: `Dhol entertainment for Diwali celebrations, product launches, and corporate events in ${city.name}. Make your ${country.name} event unforgettable.`,
                  icon: <EventIcon sx={{ fontSize: 40 }} />
                },
                {
                  title: "Cultural Celebrations",
                  description: `From engagement parties to birthday celebrations, our dhol players bring authentic energy to every occasion in ${city.name}.`,
                  icon: <StarIcon sx={{ fontSize: 40 }} />
                }
              ].map((service, i) => (
                <Grid item xs={12} md={4} key={i}>
                  <Card sx={{ height: '100%', '&:hover': { transform: 'translateY(-4px)', transition: '0.3s' } }}>
                    <CardContent sx={{ textAlign: 'center', p: 4 }}>
                      <Box sx={{ color: '#5C005D', mb: 2 }}>{service.icon}</Box>
                      <Typography variant="h5" sx={{ color: '#5C005D', mb: 2 }}>{service.title}</Typography>
                      <Typography variant="body1" color="text.secondary">{service.description}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Local Content */}
        <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: 'white' }}>
          <Container maxWidth="lg">
            <Grid container spacing={6} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography variant="h2" sx={{ color: '#5C005D', mb: 3, fontSize: { xs: '1.75rem', md: '3rem' } }}>
                  Dhol Players for Your {city.name} Event
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: '1.1rem' }}>
                  Imperial Drummers provides professional dhol entertainment for weddings and events
                  in {city.name}, {country.name}. Our UK-trained dhol players travel internationally
                  to deliver authentic Punjabi performances that energise your celebration.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.8, fontSize: '1.1rem' }}>
                  With over 2000 events performed worldwide, we bring unmatched experience
                  and professionalism to every {city.name} celebration. Our team coordinates
                  all travel logistics and works with your local venue to ensure a seamless experience.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={scrollToBooking}
                  color="gold"
                  sx={{
                    px: 4,
                    py: 1.5,
                    boxShadow: '0 8px 20px rgba(255, 215, 0, 0.2)',
                  }}
                >
                  Book {city.name} Dhol Players
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 4, backgroundColor: '#f8f5fb' }}>
                  <Typography variant="h5" sx={{ color: '#5C005D', mb: 3 }}>
                    What's Included
                  </Typography>
                  <List>
                    {[
                      "Professional dhol players (solo to full ensemble)",
                      "Authentic traditional costumes",
                      "Premium dhol instruments",
                      "International travel & logistics",
                      "Coordination with venue & planners",
                      "Flexible performance duration"
                    ].map((item, i) => (
                      <ListItem key={i}>
                        <ListItemIcon>
                          <CheckIcon sx={{ color: '#5C005D' }} />
                        </ListItemIcon>
                        <ListItemText primary={item} />
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* Sibling Cities */}
        {siblingCities.length > 0 && (
          <Box sx={{ py: { xs: 4, md: 6 }, backgroundColor: '#f8f5fb' }}>
            <Container maxWidth="lg">
              <Typography variant="h4" align="center" sx={{ color: '#5C005D', mb: 4 }}>
                Also Serving in {country.name}
              </Typography>
              <Grid container spacing={3} justifyContent="center">
                {siblingCities.map((siblingCity) => (
                  <Grid item xs={12} sm={4} md={3} key={siblingCity.slug}>
                    <Card
                      component={Link}
                      to={`/dhol-players/${continent.slug}/${country.slug}/${siblingCity.slug}`}
                      sx={{
                        textDecoration: 'none',
                        display: 'block',
                        '&:hover': { transform: 'translateY(-4px)', transition: '0.3s' }
                      }}
                    >
                      <CardContent sx={{ textAlign: 'center' }}>
                        <LocationIcon sx={{ color: '#5C005D', fontSize: 28, mb: 1 }} />
                        <Typography variant="h6" sx={{ color: '#5C005D', fontSize: '0.95rem' }}>
                          Dhol Players {siblingCity.name}
                        </Typography>
                        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#5C005D' }}>
                          <Typography variant="body2">View</Typography>
                          <ArrowIcon sx={{ fontSize: 14, ml: 0.5 }} />
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Container>
          </Box>
        )}

        {/* Booking Form */}
        <Box sx={{ py: { xs: 5, md: 8 }, backgroundColor: 'white' }}>
          <BookingForm />
        </Box>

        <Footer />
      </Box>
    </>
  );
};

export default InternationalCityPage;
